import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/apiService';

interface EligibilityRequest {
  id: number;
  userId: number;
  requestType: string;
  currentBalance: number;
  riskScore: number;
  maxAllocation: number;
  eligibilityReasons: string;
  status: string;
  adminNotes?: string;
  approvedBy?: number;
  approvedAt?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
}

interface EligibilityOverride {
  id: number;
  userId: number;
  overrideType: string;
  originalEligibility: boolean;
  overrideEligibility: boolean;
  originalMaxAllocation: number;
  overrideMaxAllocation: number;
  reason: string;
  adminNotes?: string;
  createdBy: number;
  expiresAt?: string;
  isActive: boolean;
  createdAt: string;
}

interface DashboardStats {
  pendingRequests: number;
  approvedRequests: number;
  rejectedRequests: number;
  activeOverrides: number;
  totalRequests: number;
}

const EligibilityManagementPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'requests' | 'overrides'>('dashboard');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [requests, setRequests] = useState<EligibilityRequest[]>([]);
  const [overrides, setOverrides] = useState<EligibilityOverride[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<EligibilityRequest | null>(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  // Fetch dashboard statistics
  const fetchStats = async () => {
    try {
      const response = await apiService.get('/admin/eligibility/dashboard/stats');
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  // Fetch pending requests
  const fetchPendingRequests = async () => {
    setLoading(true);
    try {
      const response = await apiService.get('/admin/eligibility/requests/pending');
      if (response.success && response.data) {
        setRequests(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch pending requests:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch active overrides
  const fetchActiveOverrides = async () => {
    setLoading(true);
    try {
      const response = await apiService.get('/admin/eligibility/overrides/active');
      if (response.success && response.data) {
        setOverrides(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch active overrides:', error);
    } finally {
      setLoading(false);
    }
  };

  // Approve request
  const approveRequest = async (requestId: number) => {
    try {
      const response = await apiService.post(`/admin/eligibility/requests/${requestId}/approve`, {
        adminUserId: 1, // Mock admin user ID
        adminNotes: adminNotes
      });
      
      if (response.success) {
        alert('Request approved successfully');
        setShowApprovalModal(false);
        setAdminNotes('');
        fetchPendingRequests();
        fetchStats();
      } else {
        alert('Failed to approve request');
      }
    } catch (error) {
      console.error('Failed to approve request:', error);
      alert('Failed to approve request');
    }
  };

  // Reject request
  const rejectRequest = async (requestId: number) => {
    try {
      const response = await apiService.post(`/admin/eligibility/requests/${requestId}/reject`, {
        adminUserId: 1, // Mock admin user ID
        rejectionReason: rejectionReason,
        adminNotes: adminNotes
      });
      
      if (response.success) {
        alert('Request rejected successfully');
        setShowApprovalModal(false);
        setRejectionReason('');
        setAdminNotes('');
        fetchPendingRequests();
        fetchStats();
      } else {
        alert('Failed to reject request');
      }
    } catch (error) {
      console.error('Failed to reject request:', error);
      alert('Failed to reject request');
    }
  };

  // Deactivate override
  const deactivateOverride = async (overrideId: number) => {
    if (!window.confirm('Are you sure you want to deactivate this override?')) {
      return;
    }

    try {
      const response = await apiService.post(`/admin/eligibility/overrides/${overrideId}/deactivate`, {
        adminUserId: 1 // Mock admin user ID
      });
      
      if (response.success) {
        alert('Override deactivated successfully');
        fetchActiveOverrides();
        fetchStats();
      } else {
        alert('Failed to deactivate override');
      }
    } catch (error) {
      console.error('Failed to deactivate override:', error);
      alert('Failed to deactivate override');
    }
  };

  useEffect(() => {
    fetchStats();
    if (activeTab === 'requests') {
      fetchPendingRequests();
    } else if (activeTab === 'overrides') {
      fetchActiveOverrides();
    }
  }, [activeTab]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return '#f59e0b';
      case 'APPROVED': return '#10b981';
      case 'REJECTED': return '#ef4444';
      case 'EXPIRED': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getRequestTypeColor = (type: string) => {
    switch (type) {
      case 'INITIAL_APPLICATION': return '#3b82f6';
      case 'BALANCE_INCREASE': return '#8b5cf6';
      case 'RISK_OVERRIDE': return '#f59e0b';
      case 'MANUAL_REVIEW': return '#ef4444';
      case 'APPEAL': return '#10b981';
      default: return '#6b7280';
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '600' }}>Eligibility Management</h2>
        <p style={{ margin: '0', color: '#6b7280' }}>Manage user eligibility requests and overrides</p>
      </div>

      {/* Tab Navigation */}
      <div style={{ 
        display: 'flex', 
        borderBottom: '2px solid #e5e7eb',
        marginBottom: '24px'
      }}>
        {[
          { id: 'dashboard', label: 'Dashboard' },
          { id: 'requests', label: 'Pending Requests' },
          { id: 'overrides', label: 'Active Overrides' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === tab.id ? '#3b82f6' : 'transparent',
              color: activeTab === tab.id ? 'white' : '#374151',
              cursor: 'pointer',
              borderBottom: activeTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
              fontWeight: activeTab === tab.id ? '600' : '400'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div>
          {stats && (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#fef3c7', 
                borderRadius: '8px',
                border: '1px solid #f59e0b'
              }}>
                <div style={{ fontSize: '14px', color: '#92400e', marginBottom: '4px' }}>Pending Requests</div>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#92400e' }}>{stats.pendingRequests}</div>
              </div>
              
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#d1fae5', 
                borderRadius: '8px',
                border: '1px solid #10b981'
              }}>
                <div style={{ fontSize: '14px', color: '#065f46', marginBottom: '4px' }}>Approved Requests</div>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#065f46' }}>{stats.approvedRequests}</div>
              </div>
              
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#fee2e2', 
                borderRadius: '8px',
                border: '1px solid #ef4444'
              }}>
                <div style={{ fontSize: '14px', color: '#991b1b', marginBottom: '4px' }}>Rejected Requests</div>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#991b1b' }}>{stats.rejectedRequests}</div>
              </div>
              
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#e0e7ff', 
                borderRadius: '8px',
                border: '1px solid #6366f1'
              }}>
                <div style={{ fontSize: '14px', color: '#3730a3', marginBottom: '4px' }}>Active Overrides</div>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#3730a3' }}>{stats.activeOverrides}</div>
              </div>
            </div>
          )}

          <div style={{ 
            padding: '20px', 
            backgroundColor: '#f9fafb', 
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px' }}>Quick Actions</h3>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setActiveTab('requests')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Review Pending Requests
              </button>
              <button
                onClick={() => setActiveTab('overrides')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#8b5cf6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Manage Overrides
              </button>
              <button
                onClick={() => {
                  // On dashboard, only refresh stats. Each tab has its own refresh button.
                  fetchStats();
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Refresh Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Requests Tab */}
      {activeTab === 'requests' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: '0', fontSize: '18px' }}>Pending Eligibility Requests</h3>
            <button
              onClick={fetchPendingRequests}
              disabled={loading}
              style={{
                padding: '8px 16px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div>Loading requests...</div>
            </div>
          ) : requests.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ fontSize: '16px', color: '#6b7280' }}>No pending requests</div>
            </div>
          ) : (
            <div style={{ 
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              overflow: 'hidden'
            }}>
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr',
                gap: '16px',
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderBottom: '1px solid #e5e7eb',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                <div>User ID</div>
                <div>Type</div>
                <div>Balance</div>
                <div>Risk Score</div>
                <div>Max Allocation</div>
                <div>Actions</div>
              </div>
              
              {requests.map(request => (
                <div key={request.id} style={{ 
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr',
                  gap: '16px',
                  padding: '16px',
                  borderBottom: '1px solid #f3f4f6',
                  alignItems: 'center'
                }}>
                  <div style={{ fontWeight: '500' }}>{request.userId}</div>
                  <div>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: getRequestTypeColor(request.requestType) + '20',
                      color: getRequestTypeColor(request.requestType)
                    }}>
                      {request.requestType.replace('_', ' ')}
                    </span>
                  </div>
                  <div>${request.currentBalance.toLocaleString()}</div>
                  <div>{request.riskScore}/100</div>
                  <div>${request.maxAllocation.toLocaleString()}</div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => {
                        setSelectedRequest(request);
                        setShowApprovalModal(true);
                      }}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Review
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Overrides Tab */}
      {activeTab === 'overrides' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: '0', fontSize: '18px' }}>Active Overrides</h3>
            <button
              onClick={fetchActiveOverrides}
              disabled={loading}
              style={{
                padding: '8px 16px',
                backgroundColor: '#8b5cf6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div>Loading overrides...</div>
            </div>
          ) : overrides.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ fontSize: '16px', color: '#6b7280' }}>No active overrides</div>
            </div>
          ) : (
            <div style={{ 
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              overflow: 'hidden'
            }}>
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr',
                gap: '16px',
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderBottom: '1px solid #e5e7eb',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                <div>User ID</div>
                <div>Type</div>
                <div>Original</div>
                <div>Override</div>
                <div>Expires</div>
                <div>Actions</div>
              </div>
              
              {overrides.map(override => (
                <div key={override.id} style={{ 
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr',
                  gap: '16px',
                  padding: '16px',
                  borderBottom: '1px solid #f3f4f6',
                  alignItems: 'center'
                }}>
                  <div style={{ fontWeight: '500' }}>{override.userId}</div>
                  <div>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: '#8b5cf620',
                      color: '#8b5cf6'
                    }}>
                      {override.overrideType.replace('_', ' ')}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px' }}>
                    <div>Eligible: {override.originalEligibility ? 'Yes' : 'No'}</div>
                    <div>Max: ${override.originalMaxAllocation.toLocaleString()}</div>
                  </div>
                  <div style={{ fontSize: '12px' }}>
                    <div>Eligible: {override.overrideEligibility ? 'Yes' : 'No'}</div>
                    <div>Max: ${override.overrideMaxAllocation.toLocaleString()}</div>
                  </div>
                  <div style={{ fontSize: '12px' }}>
                    {override.expiresAt ? new Date(override.expiresAt).toLocaleDateString() : 'Never'}
                  </div>
                  <div>
                    <button
                      onClick={() => deactivateOverride(override.id)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Deactivate
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedRequest && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px' }}>Review Eligibility Request</h3>
            
            <div style={{ marginBottom: '16px' }}>
              <div style={{ marginBottom: '8px' }}><strong>User ID:</strong> {selectedRequest.userId}</div>
              <div style={{ marginBottom: '8px' }}><strong>Type:</strong> {selectedRequest.requestType}</div>
              <div style={{ marginBottom: '8px' }}><strong>Current Balance:</strong> ${selectedRequest.currentBalance.toLocaleString()}</div>
              <div style={{ marginBottom: '8px' }}><strong>Risk Score:</strong> {selectedRequest.riskScore}/100</div>
              <div style={{ marginBottom: '8px' }}><strong>Max Allocation:</strong> ${selectedRequest.maxAllocation.toLocaleString()}</div>
              <div style={{ marginBottom: '8px' }}><strong>Reasons:</strong> {selectedRequest.eligibilityReasons}</div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Admin Notes:</label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minHeight: '80px'
                }}
                placeholder="Add notes about your decision..."
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setAdminNotes('');
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => rejectRequest(selectedRequest.id)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Reject
              </button>
              <button
                onClick={() => approveRequest(selectedRequest.id)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EligibilityManagementPanel;
